/**
 * The single "Refresh data" control for every Subscriptions tab (Oct 2026).
 * Rendered once by SubscriptionsSection above the tabs; admins see the button
 * + live progress, everyone gets the data-version signal.
 *
 * Runs `subscriptionRefreshAll` (revenue → subscribers → stats, the same
 * pipeline as the 07:25 cron) and watches `/subscriptionRefreshAll/latest`, so
 * the state is shared across tabs, admins and page reloads. When a run (or a
 * late revenue step) finishes, `useSubscriptionDataVersion()` ticks and the
 * pages reload their data.
 */
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import {
  COLLECTIONS,
  SUBSCRIPTION_REFRESH_STEPS,
  SUBSCRIPTION_REFRESH_STEP_LABELS,
  type SubscriptionRefreshAllJob,
} from '@daaily/shared';
import { db } from '../lib/firebase';
import { useProfile } from '../profile/ProfileProvider';
import { callSubscriptionRefreshAll } from './callables';

/** Server side considers a run older than this dead (refreshAll.ts REFRESH_ALL_STALE_MS). */
const STALE_MS = 25 * 60 * 1000;

const VersionContext = createContext(0);
/** Ticks when fresh Subscriptions data has landed — add it to a page's load deps. */
export const useSubscriptionDataVersion = () => useContext(VersionContext);

const ms = (t: { toMillis?: () => number } | null | undefined) => (t && typeof t.toMillis === 'function' ? t.toMillis() : 0);
const when = (t: number) => new Date(t).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/** Fingerprint of "what data has landed": the run's end + the revenue step's end. */
const landedKey = (j: SubscriptionRefreshAllJob | null) =>
  j ? `${ms(j.finishedAt)}|${ms(j.steps.revenue?.finishedAt)}|${ms(j.steps.health?.finishedAt)}|${ms(j.steps.stats?.finishedAt)}|${ms(j.steps.promotions?.finishedAt)}` : '';

export function SubscriptionsRefresh({ children }: { children: ReactNode }) {
  const { state } = useProfile();
  const isAdmin = state.status === 'ready' && state.isAdmin;
  const [job, setJob] = useState<SubscriptionRefreshAllJob | null>(null);
  const [version, setVersion] = useState(0);
  const [starting, setStarting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const firstKey = useRef<string | null>(null);

  useEffect(() => {
    if (!isAdmin) return;
    firstKey.current = null;
    return onSnapshot(
      doc(db, COLLECTIONS.SUBSCRIPTION_REFRESH_ALL, 'latest'),
      (snap) => {
        const j = snap.exists() ? (snap.data() as SubscriptionRefreshAllJob) : null;
        setJob(j);
        // Tick only on CHANGES after the first snapshot (pages already loaded once).
        const key = landedKey(j);
        if (firstKey.current === null) firstKey.current = key;
        else if (key !== firstKey.current) { firstKey.current = key; setVersion((v) => v + 1); }
      },
      () => setJob(null),
    );
  }, [isAdmin]);

  const running = !!job && job.status === 'running' && Date.now() - ms(job.startedAt) < STALE_MS;
  const revenuePending = job?.steps.revenue?.status === 'pending';
  const failedSteps = job && !running
    ? SUBSCRIPTION_REFRESH_STEPS.filter((k) => job.steps[k]?.status === 'failed')
    : [];

  const onRefresh = async () => {
    setErr(null);
    setStarting(true);
    try {
      // The server writes progress to the job doc as it goes; the call itself
      // can take minutes, so don't block on it — just surface a real error.
      const p = callSubscriptionRefreshAll({});
      setTimeout(() => setStarting(false), 4000);
      await p;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      // A client-side deadline just means we stopped listening; the run continues.
      if (!/deadline/i.test(msg)) setErr(msg);
    } finally {
      setStarting(false);
    }
  };

  const stepText = (k: (typeof SUBSCRIPTION_REFRESH_STEPS)[number]) => {
    const s = job!.steps[k]?.status ?? 'queued'; // a run from before the step existed has no entry
    const mark = s === 'done' ? '✓' : s === 'failed' ? '✗' : s === 'running' ? '…' : s === 'pending' ? '(Stripe preparing)' : '';
    return `${SUBSCRIPTION_REFRESH_STEP_LABELS[k]} ${mark}`.trim();
  };

  const by = job ? (job.trigger === 'cron' ? 'daily refresh' : job.requestedByEmail.split('@')[0]) : '';

  return (
    <VersionContext.Provider value={version}>
      {isAdmin && (
        <div className="w-full max-w-page px-4 md:px-8 pt-3">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            <button onClick={() => void onRefresh()} disabled={running || starting}
              className="btn-primary !py-1.5 !font-normal text-sm disabled:opacity-50"
              title="Pull the latest from Stripe, Piano and BigQuery for every Subscriptions tab">
              {running || starting ? 'Refreshing…' : 'Refresh data'}
            </button>
            <span className="text-fg-muted">
              {running ? (
                <>Refreshing for all tabs · {SUBSCRIPTION_REFRESH_STEPS.map(stepText).join(' · ')} · you can leave this page</>
              ) : job ? (
                <>Last refreshed {when(ms(job.finishedAt) || ms(job.startedAt))} by {by}</>
              ) : (
                <>Refreshes daily at 07:25</>
              )}
            </span>
          </div>
          {!running && revenuePending && (
            <p className="text-xs text-info mt-1">Revenue: Stripe is still preparing the report. It finishes automatically, usually within minutes; the other tabs are already up to date.</p>
          )}
          {failedSteps.length > 0 && (
            <p className="text-xs text-danger mt-1">
              {failedSteps.map((k) => `${SUBSCRIPTION_REFRESH_STEP_LABELS[k]} didn't refresh: ${job!.steps[k]?.error ?? 'unknown error'}`).join(' · ')}
            </p>
          )}
          {err && <p className="text-xs text-danger mt-1">{err}</p>}
        </div>
      )}
      {children}
    </VersionContext.Provider>
  );
}
